/* =====================================================
   CONFIG
===================================================== */

import { beginCell, Address, Cell, Dictionary } from "@ton/core";

import {
  storeBuyTicket,
  storeRegister,
  storeBuyLuckyTicket,
  storeBuyMegaTicket,
  storeOwnerSetMegaConfig,
  storeOwnerActivateMega,
} from "../ton/LotoTon_MultiLevelLottery";
import {
  areSameTonAddress,
  clearStoredReferralAddress,
  getStoredReferralAddress,
} from "../utils/referral";

export const CONTRACT_ADDRESS =
  (() => {
    const rawAddress = "EQBPrJwkavRybAy0nQarZUps0pKiSDTVvzFq-a8q2N4HAJoQ";
    try {
      return Address.parse(rawAddress).toString({
        urlSafe: true,
        bounceable: true,
        testOnly: false,
      });
    } catch (error) {
      console.error("Invalid CONTRACT_ADDRESS configured:", error);
      return rawAddress;
    }
  })();

const TONCENTER_BASE_URL =
  (process.env.REACT_APP_TONCENTER_BASE_URL || "https://toncenter.com/api/v2/jsonRPC").trim();
const TONCENTER_API_KEY = (process.env.REACT_APP_TONCENTER_API_KEY || "").trim();
const TONCENTER_USE_API_KEY = (process.env.REACT_APP_TONCENTER_USE_API_KEY || "").trim() === "1";
const TONCENTER_URL_WITH_KEY = TONCENTER_API_KEY
  ? `${TONCENTER_BASE_URL}?api_key=${encodeURIComponent(TONCENTER_API_KEY)}`
  : TONCENTER_BASE_URL;
const TONCENTER_URL_NO_KEY = TONCENTER_BASE_URL;

const TTL_MS = 2000;
const MIN_INTERVAL = 2000;
const DEPLOYED_BUY_GAS_RESERVE_NANO = 100_000_000n;
const RETRY_BACKOFF_MS = [500, 1200, 2500] as const;

const isDebugEnabled = () => {
  if (process.env.NODE_ENV !== "production") return true;
  try {
    return localStorage.getItem("debugLotteryClient") === "1";
  } catch {
    return false;
  }
};

const logDebug = (...args: any[]) => {
  if (isDebugEnabled()) {
    console.log(...args);
  }
};

/* =====================================================
   GLOBAL SINGLETON (HOT RELOAD SAFE)
===================================================== */

type CacheEntry = { data: any[]; ts: number; ttlMs: number };
type PriceBatchEntry = { levels: number[]; prices: bigint[]; ts: number; ttlMs: number };

const g = window as any;

if (!g.__TON_CACHE__) {
  g.__TON_CACHE__ = new Map<string, CacheEntry>();
  g.__TON_INFLIGHT__ = new Map<string, Promise<any[] | null>>();
  g.__TON_LAST_CALL__ = 0;
}

if (!g.__TON_LEVEL_PRICE_BATCH_CACHE__) {
  g.__TON_LEVEL_PRICE_BATCH_CACHE__ = new Map<string, PriceBatchEntry>();
}

if (!g.__TON_LEVEL_PRICE_BATCH_INFLIGHT__) {
  g.__TON_LEVEL_PRICE_BATCH_INFLIGHT__ = new Map<string, Promise<bigint[]>>();
}

if (!g.__TON_RPC_QUEUE__) {
  g.__TON_RPC_QUEUE__ = Promise.resolve();
}

if (typeof g.__TONCENTER_DISABLE_API_KEY__ !== "boolean") {
  g.__TONCENTER_DISABLE_API_KEY__ = false;
}

if (typeof g.__TONCENTER_403_LOGGED__ !== "boolean") {
  g.__TONCENTER_403_LOGGED__ = false;
}

if (typeof g.__TONCENTER_BACKOFF_UNTIL__ !== "number") {
  g.__TONCENTER_BACKOFF_UNTIL__ = 0;
}

if (typeof g.__TONCENTER_429_LOGGED_UNTIL__ !== "number") {
  g.__TONCENTER_429_LOGGED_UNTIL__ = 0;
}

if (typeof g.__TONCENTER_AUTH_MODE_LOGGED__ !== "boolean") {
  g.__TONCENTER_AUTH_MODE_LOGGED__ = false;
}

const cache: Map<string, CacheEntry> = g.__TON_CACHE__;
const inFlight: Map<string, Promise<any[] | null>> = g.__TON_INFLIGHT__;
const priceBatchCache: Map<string, PriceBatchEntry> = g.__TON_LEVEL_PRICE_BATCH_CACHE__;
const priceBatchInFlight: Map<string, Promise<bigint[]>> = g.__TON_LEVEL_PRICE_BATCH_INFLIGHT__;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function runRpcRequestSerialized<T>(request: () => Promise<T>): Promise<T> {
  const queued = (g.__TON_RPC_QUEUE__ as Promise<void>).then(() => request());
  g.__TON_RPC_QUEUE__ = queued.then(() => undefined, () => undefined);
  return queued;
}

function getMethodCacheTtlMs(method: string): number {
  if (method === "getLevelPrice") return 60_000;
  if (method === "getCycleBasic") return 6_000;
  if (method === "getMegaJackpotInfo") return 6_000;
  return Math.max(TTL_MS, 4_000);
}

function isApiKeyFormatValid(key: string): boolean {
  if (!key) return false;
  if (key === "YOUR_KEY" || key === "YOUR_KEY_HERE") return false;
  return /^[A-Za-z0-9_-]{20,}$/.test(key);
}

function shouldUseToncenterApiKey() {
  return TONCENTER_USE_API_KEY && isApiKeyFormatValid(TONCENTER_API_KEY) && !g.__TONCENTER_DISABLE_API_KEY__;
}

function logToncenterAuthModeOnce() {
  if (g.__TONCENTER_AUTH_MODE_LOGGED__) return;

  if (!TONCENTER_USE_API_KEY) {
    console.warn("Toncenter authenticated mode is disabled (REACT_APP_TONCENTER_USE_API_KEY!=1). Using public mainnet endpoint.");
    g.__TONCENTER_AUTH_MODE_LOGGED__ = true;
    return;
  }

  if (!isApiKeyFormatValid(TONCENTER_API_KEY)) {
    console.warn("Toncenter API key is missing or invalid. Using public mainnet endpoint.");
    g.__TONCENTER_AUTH_MODE_LOGGED__ = true;
    return;
  }

  console.log("Toncenter authenticated mainnet endpoint is enabled.");
  g.__TONCENTER_AUTH_MODE_LOGGED__ = true;
}

function getToncenterUrl(useApiKey: boolean) {
  return useApiKey ? TONCENTER_URL_WITH_KEY : TONCENTER_URL_NO_KEY;
}

/* =====================================================
   LOW LEVEL CALL (RATE LIMIT SAFE)
===================================================== */

async function callGetMethod(method: string, stack: any[] = []): Promise<any[] | null> {
  const key = `${CONTRACT_ADDRESS}:${method}:${JSON.stringify(stack)}`;
  const now = Date.now();
  const stale = cache.get(key)?.data ?? null;

  const cached = cache.get(key);
  if (cached && now - cached.ts < cached.ttlMs) return cached.data;

  if (inFlight.has(key)) {
    logDebug(`[rpc] reuse in-flight ${method}`);
    return inFlight.get(key)!;
  }

  const promise = (async () => {
    logToncenterAuthModeOnce();

    const makeRequest = async (useApiKey: boolean) => {
      const requestUrl = getToncenterUrl(useApiKey);

      const wait = Math.max(0, MIN_INTERVAL - (Date.now() - g.__TON_LAST_CALL__));
      if (wait > 0) {
        await sleep(wait);
      }

      g.__TON_LAST_CALL__ = Date.now();

      const res = await runRpcRequestSerialized(() =>
        fetch(requestUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: 1,
            jsonrpc: "2.0",
            method: "runGetMethod",
            params: {
              address: CONTRACT_ADDRESS,
              method,
              stack,
            },
          }),
        })
      );

      let json: any = null;
      try {
        json = await res.json();
      } catch {
        json = null;
      }

      return { res, json, useApiKey };
    };

    try {
      let useApiKey = shouldUseToncenterApiKey();
      let logged429InThisCycle = false;

      for (let attempt = 0; attempt < RETRY_BACKOFF_MS.length; attempt += 1) {
        const globalBackoffUntil = Number(g.__TONCENTER_BACKOFF_UNTIL__ || 0);
        if (Date.now() < globalBackoffUntil) {
          logDebug(`[rpc] cooldown active for ${method}`);
          await sleep(globalBackoffUntil - Date.now());
        }

        let response: { res: Response; json: any; useApiKey: boolean };
        try {
          response = await makeRequest(useApiKey);
        } catch {
          if (attempt < RETRY_BACKOFF_MS.length - 1) {
            await sleep(RETRY_BACKOFF_MS[attempt]);
            continue;
          }
          return stale;
        }

        const { res, json } = response;

        if (res.status === 403 && response.useApiKey) {
          g.__TONCENTER_DISABLE_API_KEY__ = true;
          if (!g.__TONCENTER_403_LOGGED__) {
            console.warn("Toncenter API key rejected (403). Using public mainnet endpoint for this session.");
            g.__TONCENTER_403_LOGGED__ = true;
          }
          useApiKey = false;
          attempt -= 1;
          continue;
        }

        if (res.status === 429 || json?.code === 429) {
          const delay = RETRY_BACKOFF_MS[Math.min(attempt, RETRY_BACKOFF_MS.length - 1)];
          g.__TONCENTER_BACKOFF_UNTIL__ = Date.now() + delay;
          if (!logged429InThisCycle && Date.now() >= Number(g.__TONCENTER_429_LOGGED_UNTIL__ || 0)) {
            console.warn(`Toncenter rate limit (429). Backing off for ${delay}ms.`);
            g.__TONCENTER_429_LOGGED_UNTIL__ = g.__TONCENTER_BACKOFF_UNTIL__;
            logged429InThisCycle = true;
          }

          if (attempt < RETRY_BACKOFF_MS.length - 1) {
            await sleep(delay);
            continue;
          }

          return stale;
        }

        if (!res.ok) {
          if (attempt < RETRY_BACKOFF_MS.length - 1 && res.status >= 500) {
            await sleep(RETRY_BACKOFF_MS[attempt]);
            continue;
          }
          return stale;
        }

        if (json?.error) {
          return stale;
        }

        const resultStack = Array.isArray(json?.result?.stack)
          ? json.result.stack
          : Array.isArray(json?.result?.result?.stack)
            ? json.result.result.stack
            : null;

        if (!resultStack) {
          if (attempt < RETRY_BACKOFF_MS.length - 1) {
            await sleep(RETRY_BACKOFF_MS[attempt]);
            continue;
          }
          return stale;
        }

        const data = resultStack;
        if (data.length > 0) {
          cache.set(key, { data, ts: Date.now(), ttlMs: getMethodCacheTtlMs(method) });
        }
        return data;
      }

      return stale;
    } catch (e) {
      return stale;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
}

export async function getLevelPricesNano(levels: number[]): Promise<bigint[]> {
  const normalized = [...new Set(levels.map((level) => Number(level)))].sort((a, b) => a - b);
  const key = `${CONTRACT_ADDRESS}:getLevelPriceBatch:${normalized.join(",")}`;
  const now = Date.now();
  const ttlMs = 60_000;

  const cached = priceBatchCache.get(key);
  if (cached && now - cached.ts < cached.ttlMs) {
    const byLevel = new Map<number, bigint>();
    cached.levels.forEach((level, idx) => byLevel.set(level, cached.prices[idx]));
    logDebug("[price-batch] served from cache");
    return levels.map((level) => byLevel.get(Number(level)) ?? 0n);
  }

  if (priceBatchInFlight.has(key)) {
    const inFlightResult = await priceBatchInFlight.get(key)!;
    const byLevel = new Map<number, bigint>();
    normalized.forEach((level, idx) => byLevel.set(level, inFlightResult[idx]));
    logDebug("[price-batch] reuse in-flight");
    return levels.map((level) => byLevel.get(Number(level)) ?? 0n);
  }

  const promise = (async () => {
    const prices = await Promise.all(normalized.map((level) => getLevelPriceNano(level)));
    priceBatchCache.set(key, { levels: normalized, prices, ts: Date.now(), ttlMs });
    return prices;
  })();

  priceBatchInFlight.set(key, promise);

  try {
    const normalizedPrices = await promise;
    const byLevel = new Map<number, bigint>();
    normalized.forEach((level, idx) => byLevel.set(level, normalizedPrices[idx]));
    return levels.map((level) => byLevel.get(Number(level)) ?? 0n);
  } finally {
    priceBatchInFlight.delete(key);
  }
}

/* =====================================================
   TVM PARSERS (ROBUST)
===================================================== */

function extractNumber(entry: any): bigint {
  if (!entry) return 0n;

  if (Array.isArray(entry)) {
    const [type, value] = entry;

    if (type === "num") {
      if (value === "-1" || value === "-0x1") {
        return 0n;
      }
      return BigInt(value);
    }

    return 0n;
  }

  if (entry["@type"] === "tvm.stackEntryNumber") {
    const v = entry.number?.number;
    if (v === "-1" || v === "-0x1") {
      return 0n;
    }
    return BigInt(v);
  }

  return 0n;
}


function extractBool(entry: any): boolean {
  if (Array.isArray(entry) && entry.length >= 2 && entry[0] === "num") {
    const value = String(entry[1]);
    if (value === "-1" || value === "-0x1") return true;
    return BigInt(value) !== 0n;
  }

  if (entry?.["@type"] === "tvm.stackEntryNumber") {
    const value = String(entry.number?.number ?? "0");
    if (value === "-1" || value === "-0x1") return true;
    return BigInt(value) !== 0n;
  }

  return extractNumber(entry) !== 0n;
}

function extractTupleElements(entry: any): any[] | null {
  if (Array.isArray(entry) && entry[0] === "tuple") {
    return entry[1]?.elements ?? null;
  }

  if (entry?.["@type"] === "tvm.stackEntryTuple") {
    return entry.tuple?.elements ?? null;
  }

  return null;
}

function extractAddressFromSlice(entry: any): string | null {
  let bytes: string | undefined;

  if (entry?.["@type"] === "tvm.stackEntrySlice") {
    bytes = entry.slice?.bytes;
  }

  if (Array.isArray(entry) && entry[0] === "slice") {
    bytes = entry[1]?.bytes;
  }

  if (entry?.["@type"] === "tvm.stackEntryCell") {
    bytes = entry.cell?.bytes;
  }

  if (Array.isArray(entry) && entry[0] === "cell") {
    bytes = entry[1]?.bytes;
  }

  if (!bytes) return null;

  try {
    const c = Cell.fromBase64(bytes);
    const s = c.beginParse();
    const addr = s.loadAddress();
    return addr.toString({ testOnly: true, bounceable: true });
  } catch (e) {
    console.warn("Failed to decode address cell", e);
    return null;
  }
}

function makeAddressArg(addr: string) {
  const parsed = Address.parse(addr);
  const sliceBocBase64 = beginCell().storeAddress(parsed).endCell().toBoc().toString("base64");
  return ["slice", { bytes: sliceBocBase64 }];
}

/* =====================================================
   TYPES (FRONTEND DTO)
===================================================== */

export type PlayerBasicInfoDTO = {
  exists: boolean;
  userLevel: number;
  winningJackpotAmount: bigint;
  referredCount: number;
  inRowBonus: bigint;
  soldBonus: bigint;
  referralBonus: bigint;
  ticketsSoldByReferrals: number;
};

export type PlayerTicketsInfoDTO = {
  exists: boolean;
  cycleId: number;
  count: number;
};

export type CycleDTO = {
  id: number;
  ticketsSold: number;
  isFinished: boolean;
};

/* =====================================================
   NEW PARSERS (MATCHES YOUR NEW ABI)
===================================================== */

/**
 * getPlayerBasicInfo returns:
 *  - exists (bool)
 *  - userLevel (int)
 *  - winningJackpotAmount (int)
 *  - referredCount (int)
 *  - ref cell:
 *      inRowBonus (int)
 *      soldBonus (int)
 *      referralBonus (int)
 *      ref cell:
 *        ticketsSoldByReferrals (int)
 */
function parsePlayerBasicInfo(stack: any[]): PlayerBasicInfoDTO | null {
  if (!stack || stack.length === 0) return null;

  const top = stack[0];
  const elems = extractTupleElements(top) ?? stack;

  if (!elems || elems.length < 8) {
    console.warn("Bad getPlayerBasicInfo format:", stack);
    return null;
  }

  return {
    exists: extractBool(elems[0]),
    userLevel: Number(extractNumber(elems[1])),
    winningJackpotAmount: extractNumber(elems[2]),
    referredCount: Number(extractNumber(elems[3])),
    inRowBonus: extractNumber(elems[4]),
    soldBonus: extractNumber(elems[5]),
    referralBonus: extractNumber(elems[6]),
    ticketsSoldByReferrals: Number(extractNumber(elems[7])),
  };
}

function parsePlayerTicketsInfo(stack: any[]): PlayerTicketsInfoDTO | null {
  if (!stack || stack.length === 0) return null;

  const top = stack[0];
  const elems = extractTupleElements(top) ?? stack;

  if (!elems || elems.length < 3) {
    console.warn("Bad getPlayerTicketsInLevel format:", stack);
    return null;
  }

  return {
    exists: extractBool(elems[0]),
    cycleId: Number(extractNumber(elems[1])),
    count: Number(extractNumber(elems[2])),
  };
}

function parseCycle(stack: any[]): CycleDTO | null {
  if (!stack || stack.length === 0) return null;

  const maybeTuple = stack[0];
  const elems = extractTupleElements(maybeTuple);

  if (!elems) {
    if (stack.length >= 3) {
      return {
        id: Number(extractNumber(stack[0])),
        ticketsSold: Number(extractNumber(stack[1])),
        isFinished: extractBool(stack[2]),
      };
    }
    return null;
  }

  return {
    id: Number(extractNumber(elems[0])),
    ticketsSold: Number(extractNumber(elems[1])),
    isFinished: extractBool(elems[2]),
  };
}

// =======================
// DICT PARSER (ticketId -> owner)
// =======================

function extractDictBocBase64(entry: any): string | null {
  if (entry?.["@type"] === "tvm.stackEntryCell") {
    return entry.cell?.bytes ?? null;
  }
  if (Array.isArray(entry) && entry[0] === "cell") {
    return entry[1]?.bytes ?? null;
  }
  return null;
}

function parseTakenTicketIdsFromCycleStack(stack: any[]): number[] {
  if (!stack || stack.length === 0) return [];

  const top = stack[0];
  const elems = extractTupleElements(top) ?? null;

  // Cycle tuple: [id, ticketsSold, isFinished, nextTicket, ticketsDict]
  // nextTicket was added as a new field at index 3; tickets dict is now at index 4
  if (!elems || elems.length < 5) return [];

  const dictEntry = elems[4];
  const dictB64 = extractDictBocBase64(dictEntry);
  if (!dictB64) return [];

  try {
    const cell = Cell.fromBase64(dictB64);

    const slice = cell.beginParse();
    // @ts-ignore
    const dict = slice.loadDictDirect(
      // @ts-ignore
      Dictionary.Keys.BigInt(257),
      // @ts-ignore
      Dictionary.Values.Address()
    );

    const ids: number[] = [];

    for (const [key] of dict) {
      ids.push(Number(key));
    }

    return ids;
  } catch (e) {
    console.warn("Failed to parse tickets dict:", e);
    return [];
  }
}

function parseIntDictValuesFromStackEntry(entry: any): number[] {
  const dictB64 = extractDictBocBase64(entry);
  if (!dictB64) return [];

  try {
    const cell = Cell.fromBase64(dictB64);
    const slice = cell.beginParse();

    // @ts-ignore
    const dict = slice.loadDictDirect(
      // @ts-ignore
      Dictionary.Keys.BigInt(257),
      // @ts-ignore
      Dictionary.Values.BigInt(257)
    );

    const values: number[] = [];
    for (const [, value] of dict) {
      values.push(Number(value));
    }

    values.sort((a, b) => a - b);
    return values;
  } catch (e) {
    console.warn("Failed to parse int dict values:", e);
    return [];
  }
}

/* =====================================================
   GET METHODS (UPDATED TO NEW CONTRACT)
===================================================== */

export async function getOwner(): Promise<string> {
  const stack = await callGetMethod("getOwner");
  const addr = extractAddressFromSlice(stack?.[0]);
  return addr ?? "";
}

export async function getMegaJackpotNano(): Promise<bigint> {
  const stack = await callGetMethod("getMegaJackpot");
  return extractNumber(stack?.[0]);
}

/**
 * ✅ price per ticket (nanoTON)
 */
export async function getLevelPriceNano(level: number): Promise<bigint> {
  const stack = await callGetMethod("getLevelPrice", [["num", BigInt(level).toString()]]);
  if (!stack || stack.length === 0) {
    throw new Error("No data from Toncenter (rate limit or network)");
  }
  return extractNumber(stack[0]);
}

/**
 * Price multiplier k of a game level (ticket price = basePrice × k).
 */
export async function getLevelK(level: number): Promise<number> {
  const stack = await callGetMethod("getLevelConfig", [["num", BigInt(level).toString()]]);
  if (!stack || stack.length === 0) {
    throw new Error("No data from Toncenter (rate limit or network)");
  }
  const elems = extractTupleElements(stack[0]) ?? stack;
  return Number(extractNumber(elems[0]));
}

/**
 * ✅ Cycle info (optional)
 * NOTE: new contract exposes getCycleBasic (not getCycle)
 */
export async function getCycle(level: number): Promise<CycleDTO | null> {
  logDebug(`[getCycle] calling getCycleBasic for level=${level}, contract=${CONTRACT_ADDRESS}`);
  const stack = await callGetMethod("getCycleBasic", [["num", BigInt(level).toString()]]);
  logDebug(`[getCycle] raw stack:`, JSON.stringify(stack));
  if (!stack || stack.length === 0) return null;
  const result = parseCycle(stack);
  logDebug(`[getCycle] parsed:`, result);
  return result;
}
export async function getTakenTickets(level: number): Promise<number[]> {
  const stack = await callGetMethod("getCycleBasic", [["num", BigInt(level).toString()]]);
  if (!stack || stack.length === 0) return [];

  const ids = parseTakenTicketIdsFromCycleStack(stack);

  ids.sort((a, b) => a - b);

  return ids;
}
/**
 * ✅ sold = cycle.ticketsSold
 */
export async function getCycleSold(level: number): Promise<number> {
  const cycle = await getCycle(level);
  const sold = cycle?.ticketsSold ?? 0;
  logDebug(`[getCycleSold] level=${level} sold=${sold} total=777 available=${777 - sold} progress=${Math.floor(sold / 777 * 100)}%`);
  return sold;
}

/**
 * ✅ NEW: getPlayerBasicInfo(user)
 */
export async function getPlayerBasicInfo(userAddr: string): Promise<PlayerBasicInfoDTO | null> {
  const stack = await callGetMethod("getPlayerBasicInfo", [makeAddressArg(userAddr)]);
  if (!stack || stack.length === 0) return null;
  return parsePlayerBasicInfo(stack);
}

/**
 * ✅ NEW: getPlayerReferrer(user) -> optional address
 */
export async function getPlayerReferrer(userAddr: string): Promise<string | null> {
  const stack = await callGetMethod("getPlayerReferrer", [makeAddressArg(userAddr)]);
  if (!stack || stack.length === 0) return null;

  // optional: can be null
  if (stack[0]?.["@type"] === "tvm.stackEntryNull") return null;
  if (Array.isArray(stack[0]) && stack[0][0] === "null") return null;

  const addr = extractAddressFromSlice(stack[0]);
  return addr ?? null;
}

/**
 * ✅ NEW: getPlayerTicketsInLevel(user, level)
 */
export async function getPlayerTicketsInLevel(
  userAddr: string,
  level: number
): Promise<PlayerTicketsInfoDTO | null> {
  const stack = await callGetMethod("getPlayerTicketsInLevel", [
    makeAddressArg(userAddr),
    ["num", BigInt(level).toString()],
  ]);

  if (!stack || stack.length === 0) return null;
  return parsePlayerTicketsInfo(stack);
}

export async function getPlayerMegaTickets(
  userAddr: string
): Promise<PlayerTicketsInfoDTO | null> {
  const stack = await callGetMethod("getPlayerMegaTickets", [makeAddressArg(userAddr)]);
  if (!stack || stack.length === 0) return null;
  return parsePlayerTicketsInfo(stack);
}

export async function getUserMegaTicketIds(userAddr: string): Promise<number[]> {
  const stack = await callGetMethod("getUserMegaTicketIds", [makeAddressArg(userAddr)]);
  if (!stack || stack.length === 0) return [];

  const top = stack[0];
  if (!top) return [];

  if (top?.["@type"] === "tvm.stackEntryNull") return [];
  if (Array.isArray(top) && top[0] === "null") return [];

  return parseIntDictValuesFromStackEntry(top);
}

/**
 * ✅ NEW: hasPlayerTicket(user, level, ticketId) -> bool
 */
export async function hasPlayerTicket(
  userAddr: string,
  level: number,
  ticketId: number
): Promise<boolean> {
  const stack = await callGetMethod("hasPlayerTicket", [
    makeAddressArg(userAddr),
    ["num", BigInt(level).toString()],
    ["num", BigInt(ticketId).toString()],
  ]);

  if (!stack || stack.length === 0) return false;

  // can be direct bool as number
  return extractBool(stack[0]);
}

/**
 * ✅ wait helper
 */
export async function waitForSoldIncrease(
  level: number,
  prevSold: number,
  timeoutMs = 60000
): Promise<number> {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, 2000));

    cache.clear();

    const sold = await getCycleSold(level);

    logDebug("Polling sold:", sold);

    if (sold > prevSold) {
      return sold;
    }
  }

  throw new Error("Timeout waiting for blockchain update");
}

/* =====================================================
   SEND (REGISTER / BUY)
===================================================== */

export async function register(tonConnectUI: any, referrerAddress: string) {
  const userAddress = Address.parse(tonConnectUI.account.address);

  const payload = beginCell()
    .store(
      storeRegister({
        $$type: "Register",
        user: userAddress,
        referrer: Address.parse(referrerAddress),
      })
    )
    .endCell();

  await tonConnectUI.sendTransaction({
    validUntil: Math.floor(Date.now() / 1000) + 300,
    messages: [
      {
        address: CONTRACT_ADDRESS,
        amount: "30000000",
        payload: payload.toBoc().toString("base64"),
      },
    ],
  });

  clearStoredReferralAddress();
}

type ContractMessage = {
  address: string;
  amount: string;
  payload: string;
};

async function getPendingReferralAddress(userAddress: string): Promise<string | null> {
  const storedReferralAddress = getStoredReferralAddress();

  if (!storedReferralAddress) {
    return null;
  }

  if (areSameTonAddress(storedReferralAddress, userAddress)) {
    clearStoredReferralAddress();
    return null;
  }

  try {
    const currentReferrer = await getPlayerReferrer(userAddress);

    if (currentReferrer) {
      clearStoredReferralAddress();
      return null;
    }
  } catch (error) {
    console.warn("Failed to resolve current referrer before purchase:", error);
    return null;
  }

  return storedReferralAddress;
}

async function buildReferralRegistrationMessage(tonConnectUI: any): Promise<ContractMessage | null> {
  const userAddress = tonConnectUI?.account?.address;

  if (!userAddress) {
    return null;
  }

  const referralAddress = await getPendingReferralAddress(userAddress);

  if (!referralAddress) {
    return null;
  }

  const payload = beginCell()
    .store(
      storeRegister({
        $$type: "Register",
        user: Address.parse(userAddress),
        referrer: Address.parse(referralAddress),
      })
    )
    .endCell();

  return {
    address: CONTRACT_ADDRESS,
    amount: "30000000",
    payload: payload.toBoc().toString("base64"),
  };
}

export async function buyTicket(
  tonConnectUI: any,
  level: number,
  count: number
) {
  const pricePerTicket = await getLevelPriceNano(level);
  const totalPrice = pricePerTicket * BigInt(count);
  const totalAmount = totalPrice + DEPLOYED_BUY_GAS_RESERVE_NANO;

  const payload = beginCell()
    .store(
      storeBuyTicket({
        $$type: "BuyTicket",
        level: BigInt(level),
        count: BigInt(count),
      })
    )
    .endCell();

  const referralRegistrationMessage = await buildReferralRegistrationMessage(tonConnectUI);

  const tx = await tonConnectUI.sendTransaction({
    validUntil: Math.floor(Date.now() / 1000) + 1200,
    messages: [
      ...(referralRegistrationMessage ? [referralRegistrationMessage] : []),
      {
        address: CONTRACT_ADDRESS,
        amount: totalAmount.toString(),
        payload: payload.toBoc().toString("base64"),
      },
    ],
  });

  if (referralRegistrationMessage) {
    clearStoredReferralAddress();
  }

  return tx; // 🔥 ОБЯЗАТЕЛЬНО
}

export async function buyLuckyTicket(
  tonConnectUI: any,
  level: number,
  ticketId: number
) {
  const rawPrice = await getLevelPriceNano(level);
  const pricePerTicket = BigInt(rawPrice ?? 0);

  const totalAmount = pricePerTicket + DEPLOYED_BUY_GAS_RESERVE_NANO;

  const payload = beginCell()
    .store(
      storeBuyLuckyTicket({
        $$type: "BuyLuckyTicket",
        level: BigInt(level),
        ticketId: BigInt(ticketId),
      })
    )
    .endCell();

  const referralRegistrationMessage = await buildReferralRegistrationMessage(tonConnectUI);

  const tx = await tonConnectUI.sendTransaction({
    validUntil: Math.floor(Date.now() / 1000) + 1200,
    messages: [
      ...(referralRegistrationMessage ? [referralRegistrationMessage] : []),
      {
        address: CONTRACT_ADDRESS,
        amount: totalAmount.toString(),
        payload: payload.toBoc().toString("base64"),
      },
    ],
  });

  if (referralRegistrationMessage) {
    clearStoredReferralAddress();
  }

  return tx; // 🔥 ОБЯЗАТЕЛЬНО
}

export type UITicket = {
  ticket_number: string;
  is_active: boolean;
  wallet_address: string;
  level: number;
  cycleId: number;
};

export async function getPlayerBuyBonus(
  userAddr: string,
  level: number
): Promise<bigint> {
  const stack = await callGetMethod("getPlayerBuyBonus", [
    makeAddressArg(userAddr),
    ["num", BigInt(level).toString()],
  ]);

  return extractNumber(stack?.[0]);
}

export async function getPlayerConsecutiveWins(
  userAddr: string,
  level: number
): Promise<number> {
  const stack = await callGetMethod("getPlayerConsecutiveWins", [
    makeAddressArg(userAddr),
    ["num", BigInt(level).toString()],
  ]);

  return Number(extractNumber(stack?.[0]));
}

export async function getPlayerLastWinCycle(
  userAddr: string,
  level: number
): Promise<number> {
  const stack = await callGetMethod("getPlayerLastWinCycle", [
    makeAddressArg(userAddr),
    ["num", BigInt(level).toString()],
  ]);

  return Number(extractNumber(stack?.[0]));
}


export type MegaJackpotInfoDTO = {
  balance: bigint;
  isActive: boolean;
  ticketsSold: number;
  ticketsRequired: number;
  ticketPrice: bigint;
  prizeAmount: bigint;
  threshold: bigint;
};

export async function getMegaJackpotInfo() {
  const stack = await callGetMethod("getMegaJackpotInfo");

  if (!stack || stack.length === 0) {
    console.warn("[getMegaJackpotInfo] Empty stack from contract");
    return null;
  }

  const top = stack[0];
  const elems = extractTupleElements(top) ?? stack;

  if (!elems || elems.length < 7) {
    console.warn("[getMegaJackpotInfo] Not enough elements in response:", { 
      stackLength: stack.length,
      elemsLength: elems?.length,
      stack,
      elems 
    });
    return null;
  }

  const result = {
    balance: extractNumber(elems[0]),
    isActive: extractBool(elems[1]),
    ticketsSold: Number(extractNumber(elems[2])),
    ticketsRequired: Number(extractNumber(elems[3])),
    ticketPrice: extractNumber(elems[4]),
    prizeAmount: extractNumber(elems[5]),
    threshold: extractNumber(elems[6]),
  };

  return result;
}

/**
 * Get Mega Jackpot ticket price in nanoTON
 */
export async function getMegaTicketPriceNano(): Promise<bigint> {
  try {
    const info = await getMegaJackpotInfo();
    if (!info) {
      throw new Error("Mega jackpot info is unavailable");
    }
    
    logDebug("[getMegaTicketPriceNano] Got mega info:", {
      isActive: info.isActive,
      ticketPrice: info.ticketPrice.toString(),
    });

    if (!info.isActive) {
      throw new Error("Mega jackpot is not active yet");
    }

    if (!info.ticketPrice || info.ticketPrice === 0n) {
      throw new Error("Mega jackpot ticket price is not configured");
    }
    
    logDebug("[getMegaTicketPriceNano] Using contract price:", info.ticketPrice.toString());
    return info.ticketPrice;
  } catch (error: any) {
    console.error("[getMegaTicketPriceNano] Error fetching price:", error);
    throw error;
  }
}

/**
 * ✅ Buy Mega Jackpot tickets
 * 
 * @param tonConnectUI - TonConnect UI instance for wallet connection
 * @param count - Number of tickets to buy (1+)
 * @returns Transaction result from wallet
 * 
 * Flow:
 * 1. Check wallet connection (should be done before calling)
 * 2. Get current Mega Jackpot ticket price
 * 3. Calculate total amount = price * count + gas reserve
 * 4. Create BuyMegaTicket payload
 * 5. Send transaction to contract
 * 6. Return transaction hash
 */
export async function buyMegaJackpotTicket(
  tonConnectUI: any,
  count: number
) {
  if (!tonConnectUI || !tonConnectUI.account) {
    throw new Error("Wallet not connected");
  }

  if (count < 1) {
    throw new Error("Count must be at least 1");
  }

  logDebug("[buyMegaJackpotTicket] Starting mega ticket purchase, count:", count);

  let pricePerTicket: bigint;
  try {
    pricePerTicket = await getMegaTicketPriceNano();
    logDebug("[buyMegaJackpotTicket] Got ticket price:", pricePerTicket.toString());
  } catch (error: any) {
    console.error("[buyMegaJackpotTicket] Failed to get price:", error);
    throw new Error(`Failed to fetch ticket price: ${error?.message || error}`);
  }

  if (!pricePerTicket || pricePerTicket === 0n) {
    console.error("[buyMegaJackpotTicket] Invalid ticket price:", pricePerTicket);
    throw new Error("Invalid ticket price");
  }

  const totalPrice = pricePerTicket * BigInt(count);
  const totalAmount = totalPrice + DEPLOYED_BUY_GAS_RESERVE_NANO;
  const formatTon = (value: bigint) => {
    const ton = Number(value) / 1e9;
    return ton < 0.01 ? ton.toFixed(6) : ton.toFixed(2);
  };

  logDebug("[buyMegaJackpotTicket] Price calculation:", {
    perTicket: formatTon(pricePerTicket) + " TON",
    count,
    totalPrice: formatTon(totalPrice) + " TON",
    withGas: formatTon(totalAmount) + " TON",
    nanoAmount: totalAmount.toString(),
  });

  const payload = beginCell()
    .store(
      storeBuyMegaTicket({
        $$type: "BuyMegaTicket",
        count: BigInt(count),
      })
    )
    .endCell();

  const referralRegistrationMessage = await buildReferralRegistrationMessage(tonConnectUI);

  logDebug("[buyMegaJackpotTicket] Sending transaction to:", CONTRACT_ADDRESS);

  const tx = await tonConnectUI.sendTransaction({
    validUntil: Math.floor(Date.now() / 1000) + 1200,
    messages: [
      ...(referralRegistrationMessage ? [referralRegistrationMessage] : []),
      {
        address: CONTRACT_ADDRESS,
        amount: totalAmount.toString(),
        payload: payload.toBoc().toString("base64"),
      },
    ],
  });

  if (referralRegistrationMessage) {
    clearStoredReferralAddress();
  }

  logDebug("[buyMegaJackpotTicket] Transaction sent successfully:", tx);
  return tx;
}

/**
 * ✅ OWNER ONLY: Set Mega Config Price to exactly 0.0005 TON
 * 
 * Formula: megaTicketPrice = basePrice (500,000) × ticketPriceMultiplier
 * To achieve exactly 0.0005 TON (500,000 nanoTON):
 *   - Use multiplier = 1
 *   - basePrice stays 500,000 nanoTON
 *   - Result: 500,000 × 1 = 500,000 = 0.0005 TON ✅
 *
 * Mega threshold target:
 *   threshold = basePrice × megaThresholdMultiplier
 *   500,000 × 400 = 200,000,000 nanoTON = 0.2 TON ✅
 * 
 * @param tonConnectUI - TonConnect UI instance (must be owner wallet)
 * @param thresholdMultiplier - Winning threshold multiplier (recommended: 400 => 0.2 TON)
 * @param defaultTicketCount - Default tickets per purchase (recommended: 1000)
 * @returns Transaction result
 */
export async function setMegaConfigPrice(
  tonConnectUI: any,
  thresholdMultiplier: number = 400,
  defaultTicketCount: number = 1000
) {
  if (!tonConnectUI || !tonConnectUI.account) {
    throw new Error("Wallet not connected");
  }

  const megaInfo = await getMegaJackpotInfo();
  if (megaInfo?.isActive) {
    throw new Error(
      `Mega Jackpot is active (${megaInfo.ticketsSold}/${megaInfo.ticketsRequired} tickets sold). ` +
      "Threshold can be changed only after current Mega cycle is finished."
    );
  }

  logDebug("[setMegaConfigPrice] Setting Mega Config:", {
    thresholdMultiplier,
    ticketPriceMultiplier: 1,
    defaultTicketCount,
    resultingPrice: "0.0005 TON (500,000 nanoTON)",
    resultingThreshold: "0.2 TON (200,000,000 nanoTON) when thresholdMultiplier=400",
  });

  const payload = beginCell()
    .store(
      storeOwnerSetMegaConfig({
        $$type: "OwnerSetMegaConfig",
        thresholdMultiplier: BigInt(thresholdMultiplier),
        ticketPriceMultiplier: 1n, // ✅ This gives us exactly basePrice * 1 = 0.0005 TON
        defaultTicketCount: BigInt(defaultTicketCount),
      })
    )
    .endCell();

  logDebug("[setMegaConfigPrice] Sending OwnerSetMegaConfig transaction");

  const tx = await tonConnectUI.sendTransaction({
    validUntil: Math.floor(Date.now() / 1000) + 1200,
    messages: [
      {
        address: CONTRACT_ADDRESS,
        amount: "100000000", // 0.1 TON for gas
        payload: payload.toBoc().toString("base64"),
      },
    ],
  });

  logDebug("[setMegaConfigPrice] Config transaction sent:", tx);
  return tx;
}

/**
 * ✅ OWNER ONLY: Activate Mega Jackpot
 * 
 * Must call setMegaConfigPrice() first!
 * 
 * @param tonConnectUI - TonConnect UI instance (must be owner wallet)
 * @param ticketCount - Number of tickets required to win (e.g., 100)
 * @param prizeAmount - Prize in nanoTON (e.g., 1_000 TON = 1_000_000_000_000n)
 * @returns Transaction result
 */
export async function activateMegaJackpot(
  tonConnectUI: any,
  ticketCount: number,
  prizeAmountNano: bigint
) {
  if (!tonConnectUI || !tonConnectUI.account) {
    throw new Error("Wallet not connected");
  }

  if (ticketCount < 1) {
    throw new Error("Ticket count must be at least 1");
  }

  if (prizeAmountNano <= 0n) {
    throw new Error("Prize amount must be greater than 0");
  }

  logDebug("[activateMegaJackpot] Activating Mega Jackpot:", {
    ticketCount,
    prizeAmountNano: prizeAmountNano.toString(),
    prizeAmountTON: (Number(prizeAmountNano) / 1e9).toFixed(2),
    resultingPrice: "0.0005 TON (500,000 nanoTON)",
  });

  const payload = beginCell()
    .store(
      storeOwnerActivateMega({
        $$type: "OwnerActivateMega",
        ticketCount: BigInt(ticketCount),
        prizeAmount: prizeAmountNano,
      })
    )
    .endCell();

  logDebug("[activateMegaJackpot] Sending OwnerActivateMega transaction");

  const tx = await tonConnectUI.sendTransaction({
    validUntil: Math.floor(Date.now() / 1000) + 1200,
    messages: [
      {
        address: CONTRACT_ADDRESS,
        amount: "100000000", // 0.1 TON for gas
        payload: payload.toBoc().toString("base64"),
      },
    ],
  });

  logDebug("[activateMegaJackpot] Activation transaction sent:", tx);
  return tx;
}
